<template>
  <div class="mmg-storefront mmg-products-page fusion-body">
    <div id="wrapper">
      <SiteHeader />

      <div
        class="fusion-page-title-bar fusion-page-title-bar-none fusion-page-title-bar-left"
      >
        <div class="fusion-page-title-row">
          <div class="fusion-page-title-wrapper">
            <div class="fusion-page-title-captions">
              <h1 class="entry-title">{{ $t('products.title') }}</h1>
            </div>
            <div class="fusion-page-title-secondary">
              <div class="fusion-breadcrumbs">
                <router-link to="/">{{ $t('common.home') }}</router-link>
                <span class="fusion-breadcrumb-sep">/</span>
                <span class="breadcrumb-leaf">{{ $t('products.title') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main id="main" role="main" class="clearfix width-100">
        <div class="fusion-row products-row-max">
          <section id="content" class="full-width products-content">
            <article class="page type-page status-publish hentry">
              <div class="post-content">
                <section class="fusion-fullwidth products-section">
                  <div class="fusion-builder-row fusion-row">
                    <div class="fusion-layout-column fusion-one-full products-column">
                      <div class="fusion-column-wrapper">
                        <div
                          class="fusion-recent-works fusion-portfolio fusion-portfolio-three fusion-portfolio-rollover"
                        >
                          <ul
                            class="fusion-filters"
                            role="menu"
                            aria-label="Product filters"
                          >
                            <li
                              v-for="filter in filters"
                              :key="filter.id"
                              role="menuitem"
                              class="fusion-filter"
                              :class="{
                                'fusion-filter-all': filter.id === 'all',
                                'fusion-active': activeFilter === filter.id
                              }"
                            >
                              <button
                                type="button"
                                class="fusion-filter-btn"
                                @click="setFilter(filter.id)"
                              >
                                {{ filter.label }}
                              </button>
                            </li>
                          </ul>

                          <p v-if="loading" class="products-empty">{{ $t('products.loading') }}</p>
                          <p v-else-if="loadError" class="products-empty">{{ loadError }}</p>

                          <div v-else class="fusion-portfolio-wrapper products-grid">
                            <router-link
                              v-for="product in products"
                              :key="product.id"
                              :to="{ name: 'ProductDetail', params: { id: product.id } }"
                              class="fusion-portfolio-post fusion-col-spacing product-card product-card-link"
                            >
                              <div class="fusion-portfolio-content-wrapper">
                                <div class="fusion-image-wrapper">
                                  <img
                                    v-if="product.mainImageUrl"
                                    :src="product.mainImageUrl"
                                    :alt="$productName(product)"
                                    class="product-card__img"
                                  />
                                  <div
                                    v-else
                                    class="product-placeholder"
                                    :aria-label="$productName(product)"
                                  >
                                    <span class="product-placeholder__label">
                                      {{ $productName(product) }}
                                    </span>
                                  </div>
                                  <div class="fusion-rollover">
                                    <div class="fusion-rollover-content">
                                      <span class="fusion-rollover-link">
                                        {{ $productName(product) }}
                                      </span>
                                      <div class="fusion-rollover-sep" />
                                      <h4 class="fusion-rollover-title">
                                        {{ $productName(product) }}
                                      </h4>
                                      <div class="fusion-rollover-categories">
                                        {{ $productCategoryName(product) }}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div class="fusion-portfolio-content">
                                  <h2 class="entry-title fusion-post-title">
                                    <span>{{ $productName(product) }}</span>
                                  </h2>
                                </div>
                              </div>
                            </router-link>
                          </div>

                          <p
                            v-if="!loading && !loadError && !products.length"
                            class="products-empty"
                          >
                            {{ $t('products.empty') }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </article>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  </div>
</template>

<script>
import SiteHeader from '@/components/SiteHeader.vue'
import SiteFooter from '@/components/SiteFooter.vue'
import { fetchPublicCategories, mapStorefrontCategories } from '@/api/categories'
import { fetchPublicProducts, mapStorefrontProduct } from '@/api/products'

const ALL_FILTER_ID = 'all'

export default {
  name: 'ProductPage',
  components: { SiteHeader, SiteFooter },
  data() {
    return {
      categoryItems: [],
      products: [],
      activeFilter: ALL_FILTER_ID,
      loading: true,
      loadError: ''
    }
  },
  computed: {
    filters() {
      void this.currentLocale
      return [
        { id: ALL_FILTER_ID, label: this.$t('common.all'), slug: null },
        ...this.categoryItems.map((cat) => ({
          id: String(cat.id),
          label: this.$categoryName(cat),
          slug: cat.slug
        }))
      ]
    }
  },
  watch: {
    currentLocale() {
      this.updatePageTitle()
    },
    '$route.query.category'(category) {
      this.syncFilterFromRoute(category)
      this.loadProducts()
    }
  },
  created() {
    this.bootstrap()
  },
  mounted() {
    document.body.classList.add('products')
    this.updatePageTitle()
  },
  beforeUnmount() {
    document.body.classList.remove('products')
  },
  methods: {
    async bootstrap() {
      await this.loadCategories()
      this.syncFilterFromRoute(this.$route.query.category)
      await this.loadProducts()
    },
    async loadCategories() {
      try {
        const res = await fetchPublicCategories()
        this.categoryItems = mapStorefrontCategories(res.data?.items || [])
        this.syncFilterFromRoute(this.$route.query.category)
      } catch {
        this.categoryItems = []
      }
    },
    syncFilterFromRoute(categorySlug) {
      if (!categorySlug) {
        this.activeFilter = ALL_FILTER_ID
        return
      }
      const match = this.filters.find((f) => f.slug === categorySlug)
      this.activeFilter = match ? match.id : ALL_FILTER_ID
    },
    activeCategorySlug() {
      if (this.activeFilter === ALL_FILTER_ID) return ''
      const filter = this.filters.find((f) => f.id === this.activeFilter)
      return filter?.slug || ''
    },
    async loadProducts() {
      this.loading = true
      this.loadError = ''
      try {
        const res = await fetchPublicProducts({
          category: this.activeCategorySlug()
        })
        this.products = (res.data?.items || []).map(mapStorefrontProduct)
      } catch (e) {
        this.loadError = e.message || this.$t('products.loadError')
        this.products = []
      } finally {
        this.loading = false
      }
    },
    setFilter(filterId) {
      this.activeFilter = filterId
      const filter = this.filters.find((f) => f.id === filterId)
      const query = filter?.slug ? { category: filter.slug } : {}
      this.$router.replace({ path: '/products', query }).catch(() => {})
    },
    updatePageTitle() {
      document.title = this.$t('pageTitle.products')
    }
  }
}
</script>

<style scoped>
.products-content {
  width: 100%;
}

.products-row-max {
  max-width: 100%;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.products-section {
  padding: 16px 6px 40px;
}

.mmg-products-page :deep(#main) {
  padding-left: 6px;
  padding-right: 6px;
}

.mmg-products-page :deep(.products-section .fusion-builder-row.fusion-row) {
  max-width: min(1680px, calc(100vw - 12px));
  padding-left: 0;
  padding-right: 0;
}

.products-empty {
  margin: 24px 0 0;
  text-align: center;
  color: var(--mmg-text, #747474);
}
</style>
